import axios from 'axios';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useClient, useCrmMeta, deleteClient } from '@/hooks/useCrm';
import { useAutoSave } from '@/hooks/useAutoSave';
import { isoToDisplay } from '@/lib/dateFormat';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PersonForm, { emptyPerson, withKey } from '@/components/PersonForm';
import DateInput from '@/components/DateInput';
import GenerateDocsDialog from '@/components/GenerateDocsDialog';
import {
  ArrowLeft, Edit3, Save, Trash2, FileText, Loader2, X, MessageSquare,
  Clock, AlertCircle, CheckCircle2, Plus, Zap, History as HistoryIcon,
  CloudOff,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS_BY_TYPE = {
  'Private Limited': { md: true, directors: true, mp: false, partners: false, proprietor: false },
  'Public':          { md: true, directors: true, mp: false, partners: false, proprietor: false },
  'OPC':             { md: true, directors: false, mp: false, partners: false, proprietor: false },
  'LLP':             { md: false, directors: false, mp: true, partners: true, proprietor: false },
  'Partnership':     { md: false, directors: false, mp: true, partners: true, proprietor: false },
  'Proprietorship':  { md: false, directors: false, mp: false, partners: false, proprietor: true },
};

const CIN_TYPES = ['Private Limited', 'Public', 'OPC'];

const STATUS_PILL = {
  'Doc Collection': 'bg-blue-50 text-blue-700 border-blue-200',
  'Doc Drafting': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Client Signing': 'bg-purple-50 text-purple-700 border-purple-200',
  'Emigrate Submission': 'bg-amber-50 text-amber-700 border-amber-200',
  'Police & Edu Verification': 'bg-amber-50 text-amber-700 border-amber-200',
  'Pending Inspection': 'bg-orange-50 text-orange-700 border-orange-200',
  'Pending Delhi': 'bg-orange-50 text-orange-700 border-orange-200',
  'Deficiency': 'bg-red-50 text-red-700 border-red-200',
  'Awaiting Approval': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'Completed': 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const InfoRow = ({ label, value }) => (
  <div className="flex flex-col">
    <span className="text-xs text-gray-500  tracking-wider">{label}</span>
    <span className="text-sm text-gray-900 font-medium mt-0.5 break-words">{value || '—'}</span>
  </div>
);

const PersonReadOnly = ({ person, showDin = true, showYog = false, dinLabel = 'DIN' }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <InfoRow label="Name" value={person.name} />
    <InfoRow label="Date of Birth" value={isoToDisplay(person.date_of_birth)} />
    <InfoRow label="Age" value={person.age} />
    {showDin && <InfoRow label={dinLabel} value={person.din} />}
    <InfoRow label="State" value={person.state} />
    <InfoRow label="District" value={person.district} />
    <InfoRow label="Aadhar" value={person.aadhar} />
    <InfoRow label="PAN" value={person.pan} />
    <InfoRow label="Education" value={person.education} />
    <InfoRow label="College" value={person.college} />
    {showYog && <InfoRow label="Year of Graduation" value={person.year_of_graduation} />}
    <InfoRow label="Address" value={person.address} />
    <InfoRow label="Police Station" value={person.police_station} />
    <div className="md:col-span-3"><InfoRow label="Police Station Address" value={person.police_station_address} /></div>
  </div>
);

const ClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const meta = useCrmMeta();
  const { client, loading, refresh, updateClient, updateDocuments } = useClient(id);

  const [editingDetails, setEditingDetails] = useState(false);
  const [editingRemark, setEditingRemark] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingDocs, setSavingDocs] = useState(false);
  const [savingRemark, setSavingRemark] = useState(false);
  const [generatingPending, setGeneratingPending] = useState(false);
  const [showGenDialog, setShowGenDialog] = useState(false);
  const [draftable, setDraftable] = useState({ pending: [], already_drafted: [] });

  const [docs, setDocs] = useState([]);
  const [remark, setRemark] = useState('');
  const [form, setForm] = useState(null); // edit form

  const openHistory = () => navigate(`/history?clientId=${id}`);

  useEffect(() => {
    if (client) {
      setDocs(client.documents || []);
      setRemark(client.remark || '');
    }
  }, [client]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    axios.get(`${API}/crm/clients/${id}/draftable`).then((r) => {
      if (!cancelled) setDraftable(r.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [id, client?.updated_at]);

  // ----- Auto-save (must run on every render — placed before early return per React rules) -----
  const autoSavePayload = useCallback(async (current) => {
    if (!current) return;
    const sec = SECTIONS_BY_TYPE[current.company.type] || SECTIONS_BY_TYPE['Private Limited'];
    await updateClient({
      company: current.company,
      start_date: current.start_date,
      managing_director: sec.md ? current.managing_director : null,
      directors: sec.directors ? current.directors : [],
      managing_partner: sec.mp ? current.managing_partner : null,
      partners: sec.partners ? current.partners : [],
      proprietor: sec.proprietor ? current.proprietor : null,
    });
  }, [updateClient]);

  const autoSave = useAutoSave(form, autoSavePayload, { delay: 1000, enabled: editingDetails && !!form });

  if (loading || !client) {
    return <div className="p-8 text-gray-500" data-testid="client-detail-loading">Loading client…</div>;
  }

  const co = client.company || {};
  const md = client.managing_director || null;
  const directors = (client.directors || []).map(withKey);
  const mp = client.managing_partner || null;
  const partners = (client.partners || []).map(withKey);
  const proprietor = client.proprietor || null;
  const sections = SECTIONS_BY_TYPE[co.type] || SECTIONS_BY_TYPE['Private Limited'];

  const overdue = client.days_remaining !== null && client.days_remaining < 0;
  const receivedCount = docs.filter((d) => d.received).length;

  const handleStatusChange = async (value) => {
    setSavingStatus(true);
    try {
      await updateClient({ status: value });
      toast.success(`Status updated to ${value}`);
    } catch {
      toast.error('Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const toggleReceived = (index) => {
    setDocs((prev) => prev.map((d, i) => {
      if (i !== index) return d;
      const received = !d.received;
      return { ...d, received, status: received ? 'Received' : 'Pending' };
    }));
  };

  const handleSaveDocs = async () => {
    setSavingDocs(true);
    try {
      await updateDocuments(docs);
      toast.success('Document checklist saved');
    } catch {
      toast.error('Failed to save documents');
    } finally {
      setSavingDocs(false);
    }
  };

  const handleSaveRemark = async () => {
    setSavingRemark(true);
    try {
      await updateClient({ remark });
      toast.success('Remark updated');
      setEditingRemark(false);
    } catch {
      toast.error('Failed to update remark');
    } finally {
      setSavingRemark(false);
    }
  };

  const startEditDetails = () => {
    setForm({
      company: { ...co },
      managing_director: md ? { ...md } : emptyPerson(),
      directors: directors.length ? directors.map((d) => ({ ...d })) : [emptyPerson()],
      managing_partner: mp ? { ...mp } : emptyPerson(),
      partners: partners.length ? partners.map((p) => ({ ...p })) : [emptyPerson()],
      proprietor: proprietor ? { ...proprietor } : emptyPerson(),
      start_date: client.start_date || '',
    });
    setEditingDetails(true);
  };

  const editSections = form ? (SECTIONS_BY_TYPE[form.company.type] || SECTIONS_BY_TYPE['Private Limited']) : sections;

  const handleDelete = async () => {
    if (!window.confirm('Delete this client and all related data?')) return;
    try {
      await deleteClient(id);
      toast.success('Client deleted');
      navigate('/clients');
    } catch {
      toast.error('Failed to delete client');
    }
  };

  const launchEditor = () => navigate(`/editor?clientId=${id}`);

  const setCoEdit = (k, v) => setForm({ ...form, company: { ...form.company, [k]: v } });

  return (
    <div className="p-8" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <button data-testid="back-btn" onClick={() => navigate('/clients')} className="p-2 rounded-full hover:bg-gray-100 shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 tracking-tight truncate">{co.company_name}</h1>
            <div className="flex items-center flex-wrap gap-3 mt-1 text-sm text-gray-500">
              <span>Sign-Up Date: {isoToDisplay(client.start_date) || '—'}</span>
              <span className="text-gray-300">·</span>
              <span className={overdue ? 'text-red-600 font-medium flex items-center gap-1' : 'flex items-center gap-1'}>
                {overdue ? <AlertCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                Deadline: {isoToDisplay(client.deadline_date) || '—'}
                {client.days_remaining !== null && (
                  <span className="ml-1">({overdue ? `${Math.abs(client.days_remaining)}d overdue` : `${client.days_remaining}d left`})</span>
                )}
              </span>
              <span className="text-gray-300">·</span>
              <span className="text-xs  tracking-wider text-gray-600">{co.type}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            data-testid="generate-pending-btn"
            onClick={() => setShowGenDialog(true)}
            disabled={draftable.pending.length === 0 && draftable.already_drafted.length === 0}
            className="h-10 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60"
            title="Select and generate RA documents"
          >
            <Zap className="w-4 h-4 mr-2" />
            Generate RA Docs
            {draftable.pending.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-white/20 rounded text-xs font-semibold">
                {draftable.pending.length}
              </span>
            )}
          </Button>
          <Button data-testid="launch-editor-btn" onClick={launchEditor} className="h-10 rounded-xl bg-blue-600 hover:bg-blue-700">
            <FileText className="w-4 h-4 mr-2" /> Launch Editor
          </Button>
          <Button
            data-testid="view-history-btn"
            onClick={openHistory}
            variant="outline"
            className="h-10 rounded-xl"
            title="View generation history for this client"
          >
            <HistoryIcon className="w-4 h-4 mr-2" /> History
          </Button>
          <Button
            data-testid="view-finance-btn"
            onClick={() => navigate(`/finance/company/${id}`)}
            variant="outline"
            className="h-10 rounded-xl"
            title="View finance for this client"
          >
            <FileText className="w-4 h-4 mr-2" /> Finance
          </Button>
          <Button data-testid="delete-client-btn" onClick={handleDelete} variant="outline" className="h-10 rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick controls row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <Label className="text-xs  tracking-wider text-gray-500">Status</Label>
          <Select value={client.status} onValueChange={handleStatusChange} disabled={savingStatus}>
            <SelectTrigger data-testid="status-select" className="mt-2 h-11"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(meta?.status_stages || []).map((s) => (
                <SelectItem key={s} value={s} data-testid={`status-option-${s.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2">
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${STATUS_PILL[client.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
              {client.status_group}
            </span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <Label className="text-xs  tracking-wider text-gray-500">Progress</Label>
          <div className="flex items-baseline gap-2 mt-2">
            <span className="text-3xl font-bold text-gray-900">{client.progress_pct}%</span>
            <span className="text-sm text-gray-500">{receivedCount} / {docs.length} received</span>
          </div>
          <div className="mt-2 w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div className="bg-blue-600 h-full transition-all" style={{ width: `${client.progress_pct}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <Label className="text-xs  tracking-wider text-gray-500">Remark</Label>
            {!editingRemark ? (
              <button data-testid="edit-remark-btn" onClick={() => setEditingRemark(true)} className="text-blue-600 hover:text-blue-700 text-xs flex items-center gap-1">
                <Edit3 className="w-3.5 h-3.5" /> Update
              </button>
            ) : (
              <button onClick={() => { setEditingRemark(false); setRemark(client.remark || ''); }} className="text-gray-500 hover:text-gray-700 text-xs flex items-center gap-1">
                <X className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
          </div>
          {editingRemark ? (
            <div className="mt-2 space-y-2">
              <Textarea data-testid="remark-textarea" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} />
              <Button data-testid="save-remark-btn" size="sm" onClick={handleSaveRemark} disabled={savingRemark} className="bg-blue-600 hover:bg-blue-700">
                {savingRemark ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
              </Button>
            </div>
          ) : (
            <div className="mt-2 text-sm text-gray-700 min-h-[2.5rem] flex items-start gap-2">
              {client.remark
                ? <><MessageSquare className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" /><span>{client.remark}</span></>
                : <span className="text-gray-400 italic">No remark yet</span>}
            </div>
          )}
        </div>
      </div>

      {/* Documentation Info */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Documentation Info</h2>
          {!editingDetails ? (
            <Button data-testid="edit-details-btn" variant="outline" size="sm" onClick={startEditDetails} className="rounded-xl">
              <Edit3 className="w-4 h-4 mr-2" /> Edit Details
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <span data-testid="autosave-status" className="text-xs flex items-center gap-1.5">
                {autoSave.status === 'saving' && (<><Loader2 className="w-3 h-3 animate-spin text-ink/50" /><span className="text-ink/50">Saving…</span></>)}
                {autoSave.status === 'saved' && (<><CheckCircle2 className="w-3 h-3 text-emerald-600" /><span className="text-emerald-600">Saved</span></>)}
                {autoSave.status === 'error' && (<><CloudOff className="w-3 h-3 text-red-500" /><span className="text-red-500">Save failed</span></>)}
                {autoSave.status === 'unsaved' && (<><Loader2 className="w-3 h-3 animate-spin text-amber-600" /><span className="text-amber-600">Unsaved changes…</span></>)}
                {autoSave.status === 'idle' && (<><span className="text-ink/40">Auto-save on</span></>)}
              </span>
              <Button variant="outline" size="sm" onClick={async () => { await autoSave.saveNow(); setEditingDetails(false); }} className="rounded-xl">
                <X className="w-4 h-4 mr-1" /> Done
              </Button>
            </div>
          )}
        </div>

        {!editingDetails ? (
          <div className="space-y-8">
            <div>
              <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Company</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoRow label="Company Name" value={co.company_name} />
                <InfoRow label="Short Name" value={co.short_name} />
                <InfoRow label="Type" value={co.type} />
                <InfoRow label="Date of Incorporation" value={isoToDisplay(co.date_of_incorporation)} />
                {CIN_TYPES.includes(co.type) && <InfoRow label="CIN" value={co.cin} />}
                <InfoRow label="Company PAN" value={co.pan} />
                <InfoRow label="GST" value={co.gst} />
                <InfoRow label="Email" value={co.email} />
                <InfoRow label="Phone" value={co.phone} />
                <InfoRow label="Networth" value={co.networth} />
                <InfoRow label="Fixed Asset" value={co.fixed_asset} />
                <InfoRow label="Liquid Asset" value={co.liquid_asset} />
                <InfoRow label="Liability" value={co.liability} />
                <InfoRow label="Area (Sq. ft.)" value={co.area_sqft} />
                <InfoRow label="State" value={co.state} />
                <InfoRow label="District" value={co.district} />
                <InfoRow label="Police Station" value={co.police_station} />
              </div>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                <InfoRow label="Address" value={co.address} />
                <InfoRow label="Police Station Address" value={co.police_station_address} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Banking</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoRow label="Bank" value={co.bank} />
                <InfoRow label="Bank Address" value={co.bank_address} />
                <InfoRow label="Account Number" value={co.account_number} />
                <InfoRow label="IFSC" value={co.ifsc} />
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Landlord</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <InfoRow label="Landlord Name" value={co.landlord_name} />
                <InfoRow label="Landlord Date of Birth" value={isoToDisplay(co.landlord_dob)} />
                <InfoRow label="Landlord Age" value={co.landlord_age} />
                <InfoRow label="Landlord Aadhar" value={co.landlord_aadhar} />
                <InfoRow label="Rental Amount" value={co.rental_amount} />
                <div className="md:col-span-4"><InfoRow label="Landlord Address" value={co.landlord_address} /></div>
              </div>
            </div>

            {sections.md && md && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Managing Director</h3>
                <PersonReadOnly person={md} showYog />
              </div>
            )}

            {sections.directors && directors.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Directors ({directors.length})</h3>
                <div className="space-y-6">
                  {directors.map((d, i) => (
                    <div key={d._key || `dir-${i}`} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                      <div className="text-sm font-medium text-gray-700 mb-2">Director {i + 1}</div>
                      <PersonReadOnly person={d} />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sections.mp && mp && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Managing Partner</h3>
                <PersonReadOnly person={mp} dinLabel="DPIN" showYog />
              </div>
            )}

            {sections.partners && partners.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Partners ({partners.length})</h3>
                <div className="space-y-6">
                  {partners.map((p, i) => (
                    <div key={p._key || `par-${i}`} className="border-t border-gray-100 pt-4 first:border-t-0 first:pt-0">
                      <div className="text-sm font-medium text-gray-700 mb-2">Partner {i + 1}</div>
                      <PersonReadOnly person={p} dinLabel="DPIN" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sections.proprietor && proprietor && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Proprietor</h3>
                <PersonReadOnly person={proprietor} showDin={false} />
              </div>
            )}
          </div>
        ) : form && (
          <div className="space-y-8">
            {/* Company edit */}
            <div>
              <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Company</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label className="text-xs">Company Name</Label><Input data-testid="edit-company-name" value={form.company.company_name} onChange={(e) => setCoEdit('company_name', e.target.value)} /></div>
                <div><Label className="text-xs">Short Name</Label><Input value={form.company.short_name || ''} onChange={(e) => setCoEdit('short_name', e.target.value)} /></div>
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={form.company.type} onValueChange={(v) => setCoEdit('type', v)}>
                    <SelectTrigger data-testid="edit-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{(meta?.company_types || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Date of Incorporation</Label><DateInput value={form.company.date_of_incorporation || ''} onChange={(v) => setCoEdit('date_of_incorporation', v)} testid="edit-doi" /></div>
                {CIN_TYPES.includes(form.company.type) && (
                  <div><Label className="text-xs">CIN</Label><Input data-testid="edit-cin" value={form.company.cin || ''} onChange={(e) => setCoEdit('cin', e.target.value)} /></div>
                )}
                <div><Label className="text-xs">PAN</Label><Input data-testid="edit-pan" value={form.company.pan || ''} onChange={(e) => setCoEdit('pan', e.target.value)} /></div>
                <div><Label className="text-xs">GST</Label><Input data-testid="edit-gst" value={form.company.gst || ''} onChange={(e) => setCoEdit('gst', e.target.value)} /></div>
                <div><Label className="text-xs">Email</Label><Input value={form.company.email || ''} onChange={(e) => setCoEdit('email', e.target.value)} /></div>
                <div><Label className="text-xs">Phone</Label><Input value={form.company.phone || ''} onChange={(e) => setCoEdit('phone', e.target.value)} /></div>
                <div><Label className="text-xs">Networth</Label><Input data-testid="edit-networth" value={form.company.networth || ''} onChange={(e) => setCoEdit('networth', e.target.value)} /></div>
                <div><Label className="text-xs">Fixed Asset</Label><Input data-testid="edit-fixed-asset" value={form.company.fixed_asset || ''} onChange={(e) => setCoEdit('fixed_asset', e.target.value)} /></div>
                <div><Label className="text-xs">Liquid Asset</Label><Input data-testid="edit-liquid-asset" value={form.company.liquid_asset || ''} onChange={(e) => setCoEdit('liquid_asset', e.target.value)} /></div>
                <div><Label className="text-xs">Liability</Label><Input data-testid="edit-liability" value={form.company.liability || ''} onChange={(e) => setCoEdit('liability', e.target.value)} /></div>
                <div><Label className="text-xs">Area (Sq. ft.)</Label><Input data-testid="edit-area-sqft" value={form.company.area_sqft || ''} onChange={(e) => setCoEdit('area_sqft', e.target.value)} /></div>
                <div><Label className="text-xs">State</Label><Input value={form.company.state || ''} onChange={(e) => setCoEdit('state', e.target.value)} /></div>
                <div><Label className="text-xs">District</Label><Input value={form.company.district || ''} onChange={(e) => setCoEdit('district', e.target.value)} /></div>
                <div className="md:col-span-3"><Label className="text-xs">Address</Label><Input value={form.company.address || ''} onChange={(e) => setCoEdit('address', e.target.value)} /></div>
                <div><Label className="text-xs">Police Station</Label><Input data-testid="edit-police-station" value={form.company.police_station || ''} onChange={(e) => setCoEdit('police_station', e.target.value)} /></div>
                <div className="md:col-span-2"><Label className="text-xs">Police Station Address</Label><Input data-testid="edit-police-station-address" value={form.company.police_station_address || ''} onChange={(e) => setCoEdit('police_station_address', e.target.value)} /></div>
              </div>

              <div className="mt-5 text-xs font-semibold tracking-wide text-gray-500  mb-3">Banking</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label className="text-xs">Bank</Label><Input value={form.company.bank || ''} onChange={(e) => setCoEdit('bank', e.target.value)} /></div>
                <div className="md:col-span-2"><Label className="text-xs">Bank Address</Label><Input value={form.company.bank_address || ''} onChange={(e) => setCoEdit('bank_address', e.target.value)} /></div>
                <div><Label className="text-xs">Account Number</Label><Input value={form.company.account_number || ''} onChange={(e) => setCoEdit('account_number', e.target.value)} /></div>
                <div><Label className="text-xs">IFSC</Label><Input value={form.company.ifsc || ''} onChange={(e) => setCoEdit('ifsc', e.target.value)} /></div>
              </div>

              <div className="mt-5 text-xs font-semibold tracking-wide text-gray-500  mb-3">Landlord</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div><Label className="text-xs">Landlord Name</Label><Input value={form.company.landlord_name || ''} onChange={(e) => setCoEdit('landlord_name', e.target.value)} /></div>
                <div className="md:col-span-2"><Label className="text-xs">Landlord Address</Label><Input value={form.company.landlord_address || ''} onChange={(e) => setCoEdit('landlord_address', e.target.value)} /></div>
                <div><Label className="text-xs">Landlord DoB</Label><DateInput value={form.company.landlord_dob || ''} onChange={(dob) => {
                  const b = new Date(dob);
                  let age = '';
                  if (!Number.isNaN(b.getTime())) {
                    const t = new Date();
                    let a = t.getFullYear() - b.getFullYear();
                    const m = t.getMonth() - b.getMonth();
                    if (m < 0 || (m === 0 && t.getDate() < b.getDate())) a -= 1;
                    age = a >= 0 ? String(a) : '';
                  }
                  setForm({ ...form, company: { ...form.company, landlord_dob: dob, landlord_age: age } });
                }} testid="edit-landlord-dob" /></div>
                <div><Label className="text-xs">Landlord Age</Label><Input value={form.company.landlord_age || ''} readOnly className="bg-gray-50" /></div>
                <div><Label className="text-xs">Landlord Aadhar</Label><Input data-testid="edit-landlord-aadhar" value={form.company.landlord_aadhar || ''} onChange={(e) => setCoEdit('landlord_aadhar', e.target.value)} /></div>
                <div><Label className="text-xs">Rental Amount</Label><Input value={form.company.rental_amount || ''} onChange={(e) => setCoEdit('rental_amount', e.target.value)} /></div>
              </div>
            </div>

            <div>
              <Label className="text-xs">Sign-Up Date</Label>
              <DateInput value={form.start_date || ''} onChange={(v) => setForm({ ...form, start_date: v })} testid="edit-sign-up-date" />
            </div>

            {editSections.md && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Managing Director</h3>
                <PersonForm value={form.managing_director} onChange={(v) => setForm({ ...form, managing_director: v })} dinLabel="DIN" showYearOfGraduation testidPrefix="edit-md" />
              </div>
            )}

            {editSections.directors && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold tracking-wide text-gray-500 ">Directors</h3>
                  <Button size="sm" variant="outline" data-testid="edit-add-director" onClick={() => setForm({ ...form, directors: [...form.directors, emptyPerson()] })} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-1" /> Add Director
                  </Button>
                </div>
                {form.directors.map((d, i) => (
                  <div key={d._key || `edit-dir-${i}`} className="border-t border-gray-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-700">Director {i + 1}</div>
                      {form.directors.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, directors: form.directors.filter((_, idx) => idx !== i) })} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <PersonForm value={d} onChange={(v) => setForm({ ...form, directors: form.directors.map((x, idx) => idx === i ? v : x) })} dinLabel="DIN" testidPrefix={`edit-director-${i}`} />
                  </div>
                ))}
              </div>
            )}

            {editSections.mp && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Managing Partner</h3>
                <PersonForm value={form.managing_partner} onChange={(v) => setForm({ ...form, managing_partner: v })} dinLabel="DPIN" showYearOfGraduation testidPrefix="edit-mp" />
              </div>
            )}

            {editSections.partners && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold tracking-wide text-gray-500 ">Partners</h3>
                  <Button size="sm" variant="outline" data-testid="edit-add-partner" onClick={() => setForm({ ...form, partners: [...form.partners, emptyPerson()] })} className="rounded-xl">
                    <Plus className="w-4 h-4 mr-1" /> Add Partner
                  </Button>
                </div>
                {form.partners.map((p, i) => (
                  <div key={p._key || `edit-par-${i}`} className="border-t border-gray-100 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-medium text-gray-700">Partner {i + 1}</div>
                      {form.partners.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => setForm({ ...form, partners: form.partners.filter((_, idx) => idx !== i) })} className="text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <PersonForm value={p} onChange={(v) => setForm({ ...form, partners: form.partners.map((x, idx) => idx === i ? v : x) })} dinLabel="DPIN" testidPrefix={`edit-partner-${i}`} />
                  </div>
                ))}
              </div>
            )}

            {editSections.proprietor && (
              <div>
                <h3 className="text-xs font-semibold tracking-wide text-gray-500  mb-3">Proprietor</h3>
                <PersonForm value={form.proprietor} onChange={(v) => setForm({ ...form, proprietor: v })} showDin={false} testidPrefix="edit-proprietor" />
              </div>
            )}
          </div>
        )}
      </section>

      {/* Documents Received */}
      <section className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Documents Received</h2>
          <Button data-testid="save-docs-btn" size="sm" onClick={handleSaveDocs} disabled={savingDocs} className="rounded-xl bg-blue-600 hover:bg-blue-700">
            {savingDocs ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {docs.map((d, idx) => (
            <label
              key={d.name}
              data-testid={`doc-row-${d.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${d.received ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
            >
              <Checkbox data-testid={`doc-check-${idx}`} checked={d.received} onCheckedChange={() => toggleReceived(idx)} />
              <span className={`text-sm flex-1 ${d.received ? 'text-emerald-800 font-medium' : 'text-gray-700'}`}>{d.name}</span>
              {d.received && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
            </label>
          ))}
        </div>
      </section>

      <GenerateDocsDialog
        open={showGenDialog}
        onOpenChange={setShowGenDialog}
        clientId={id}
        clientShortName={co.short_name || co.company_name}
        draftable={draftable}
        savedFolderId={client.drive_folder_id}
        savedFolderName={client.drive_folder_name}
        onGenerated={refresh}
      />
    </div>
  );
};

export default ClientDetail;
